/* --------------------------------------------------
                Mapbox Manager
-------------------------------------------------- */
// mapbox-gl and mapbox-gl-geocoder load from CDN (workers + version alignment).
// Stimulus map controllers import the singleton below; nothing is hung on window.

class MapboxManager {
  constructor() {
    this.apiLoaded = false;
    this.loadingPromise = null;
    this.geocoderLoaded = false;
    this.geocoderLoadingPromise = null;
    this.activeMaps = new Map();
    this.mapCache = new Map(); // Cache for map instances
    this.lazyMaps = new Map(); // Track lazy-loaded maps
    this.usageStats = {
      apiLoads: 0,
      mapCreations: 0,
      mapDestructions: 0,
      cacheHits: 0,
      cacheMisses: 0,
      lazyLoads: 0,
      errors: 0
    };
    this.loadPersistentCache();
    this.setupTurboHandlers();
    this.setupIntersectionObserver();
  }

  async loadAPI(accessToken) {
    // Always refresh the token when provided — a prior empty load must not stick
    if (accessToken && typeof window.mapboxgl !== 'undefined') {
      window.mapboxgl.accessToken = accessToken;
    }

    if (this.apiLoaded) {
      return Promise.resolve();
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if mapboxgl is already loaded (from CDN or previous load)
      if (typeof window.mapboxgl !== 'undefined') {
        if (accessToken) {
          window.mapboxgl.accessToken = accessToken;
        }
        this.apiLoaded = true;
        this.trackApiLoad();
        resolve();
        return;
      }

      // Load mapbox-gl from CDN to avoid webpack bundling issues
      const version = '3.16.0';
      const cssUrl = `https://api.mapbox.com/mapbox-gl-js/v${version}/mapbox-gl.css`;
      const jsUrl = `https://api.mapbox.com/mapbox-gl-js/v${version}/mapbox-gl.js`;

      // Load CSS first
      if (!document.querySelector(`link[href="${cssUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
      }

      // Load JavaScript
      const script = document.createElement('script');
      script.src = jsUrl;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        try {
          if (!accessToken) {
            this.loadingPromise = null;
            this.usageStats.errors++;
            reject(new Error('Mapbox access token is required'));
            return;
          }
          window.mapboxgl.accessToken = accessToken;
          this.apiLoaded = true;
          this.trackApiLoad();
          resolve();
        } catch (error) {
          this.loadingPromise = null;
          this.usageStats.errors++;
          reject(new Error(`Failed to initialize Mapbox: ${error.message}`));
        }
      };

      script.onerror = () => {
        this.loadingPromise = null;
        this.usageStats.errors++;
        reject(new Error('Failed to load Mapbox GL JS from CDN'));
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  registerMap(id, mapInstance) {
    this.activeMaps.set(id, mapInstance);
    this.trackMapCreation();
  }

  // Cache a map instance for reuse
  cacheMap(id, mapData) {
    const cacheKey = this.generateCacheKey(mapData);
    this.mapCache.set(cacheKey, {
      id,
      mapData,
      timestamp: Date.now(),
      usageCount: 0
    });
    this.savePersistentCache();
  }

  // Try to get a cached map instance
  getCachedMap(mapData, newestJobUpdatedAt = null) {
    const cacheKey = this.generateCacheKey(mapData);
    const cached = this.mapCache.get(cacheKey);

    if (cached && this.isCacheValid(cached, newestJobUpdatedAt)) {
      cached.usageCount++;
      this.trackCacheHit();
      return cached;
    }

    this.trackCacheMiss();
    return null;
  }

  // Generate cache key based on map configuration
  generateCacheKey(mapData) {
    // If a custom cache key is provided, use it
    if (mapData.cacheKey) {
      return mapData.cacheKey;
    }

    // Otherwise, generate key from configuration
    const key = {
      center: mapData.center ? [
        Math.round(mapData.center[0] * 1000000) / 1000000, // lng
        Math.round(mapData.center[1] * 1000000) / 1000000  // lat
      ] : mapData.center,
      zoom: mapData.zoom,
      style: mapData.style || 'mapbox://styles/mapbox/streets-v12',
      bearing: mapData.bearing || 0,
      pitch: mapData.pitch || 0
    };
    return JSON.stringify(key);
  }

  // Check if cache entry is still valid (1 hour TTL, or invalidated by newer job)
  isCacheValid(cached, newestJobUpdatedAt = null) {
    const TTL = 60 * 60 * 1000; // 1 hour

    // Check TTL first
    const isWithinTTL = (Date.now() - cached.timestamp) < TTL;
    if (!isWithinTTL) {
      return false;
    }

    // If newestJobUpdatedAt is provided, check if any job is newer than cache
    if (newestJobUpdatedAt) {
      const cacheTime = new Date(cached.timestamp);
      const newestJobTime = new Date(newestJobUpdatedAt);
      // If a job is newer than the cache, invalidate it
      if (newestJobTime > cacheTime) {
        return false;
      }
    }

    return true;
  }

  // Clear expired cache entries
  clearExpiredCache() {
    for (const [key, cached] of this.mapCache.entries()) {
      if (!this.isCacheValid(cached, null)) {
        this.mapCache.delete(key);
      }
    }
  }

  destroyMap(id) {
    const mapData = this.activeMaps.get(id);
    if (mapData) {
      // Clean up markers
      if (mapData.markers && Array.isArray(mapData.markers)) {
        mapData.markers.forEach(marker => {
          if (marker && marker.remove) {
            marker.remove();
          }
        });
      }

      // Clean up popups
      if (mapData.popups && Array.isArray(mapData.popups)) {
        mapData.popups.forEach(popup => {
          if (popup && popup.remove) {
            popup.remove();
          }
        });
      }

      // Clean up sources and layers
      if (mapData.map) {
        const map = mapData.map;
        // Remove all sources and layers
        if (map.getStyle()) {
          const style = map.getStyle();
          if (style.layers) {
            style.layers.forEach(layer => {
              if (map.getLayer(layer.id)) {
                map.removeLayer(layer.id);
              }
            });
          }
          if (style.sources) {
            Object.keys(style.sources).forEach(sourceId => {
              if (map.getSource(sourceId)) {
                map.removeSource(sourceId);
              }
            });
          }
        }
        map.remove();
      }

      this.activeMaps.delete(id);
      this.trackMapDestruction();
    }
  }

  destroyAllMaps() {
    this.activeMaps.forEach((mapData, id) => {
      this.destroyMap(id);
    });
  }

  setupTurboHandlers() {
    // Clean up maps before caching
    document.addEventListener('turbo:before-cache', () => {
      this.destroyAllMaps();
      this.clearExpiredCache();
    });

    // Clean up maps before rendering new content
    document.addEventListener('turbo:before-render', () => {
      this.destroyAllMaps();
    });

    // Clean up maps when navigating away
    document.addEventListener('turbo:before-visit', () => {
      this.destroyAllMaps();
    });

    // Periodic cache cleanup (every 2 minutes)
    setInterval(() => {
      this.clearExpiredCache();
    }, 2 * 60 * 1000);
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      return; // Fallback for older browsers
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadLazyMap(entry.target);
        }
      });
    }, {
      rootMargin: '50px', // Load when 50px away from viewport
      threshold: 0.1
    });
  }

  trackApiLoad() {
    this.usageStats.apiLoads++;
  }

  trackMapCreation() {
    this.usageStats.mapCreations++;
  }

  trackMapDestruction() {
    this.usageStats.mapDestructions++;
  }

  trackCacheHit() {
    this.usageStats.cacheHits++;
    this.savePersistentCache();
  }

  trackCacheMiss() {
    this.usageStats.cacheMisses++;
    this.savePersistentCache();
  }

  trackLazyLoad() {
    this.usageStats.lazyLoads++;
    this.savePersistentCache();
  }

  logUsage() {
    if (window.console && window.console.log) {
      console.log('🗺️ Mapbox Usage:', this.usageStats);
    }
  }

  getStats() {
    return { ...this.usageStats };
  }

  getCacheStats() {
    return {
      cacheSize: this.mapCache.size,
      totalCacheHits: this.usageStats.cacheHits,
      totalCacheMisses: this.usageStats.cacheMisses,
      cacheHitRate: this.usageStats.cacheHits / (this.usageStats.cacheHits + this.usageStats.cacheMisses) * 100 || 0
    };
  }

  // Clear all cache entries
  clearCache() {
    this.mapCache.clear();
    this.savePersistentCache();
  }

  // Clear persistent cache from sessionStorage
  clearPersistentCache() {
    try {
      sessionStorage.removeItem('mapboxCache');
      sessionStorage.removeItem('mapboxStats');
      this.mapCache.clear();
      this.usageStats = {
        apiLoads: 0,
        mapCreations: 0,
        mapDestructions: 0,
        cacheHits: 0,
        cacheMisses: 0,
        lazyLoads: 0,
        errors: 0
      };
      console.log('🗺️ Persistent cache cleared');
    } catch (error) {
      console.warn('Failed to clear persistent cache:', error);
    }
  }

  // Load cache from sessionStorage
  loadPersistentCache() {
    try {
      const cached = sessionStorage.getItem('mapboxCache');
      if (cached) {
        const cacheData = JSON.parse(cached);
        // Restore cache entries that are still valid
        for (const [key, entry] of Object.entries(cacheData)) {
          if (this.isCacheValid(entry)) {
            this.mapCache.set(key, entry);
          }
        }
      }

      // Load usage stats
      const stats = sessionStorage.getItem('mapboxStats');
      if (stats) {
        this.usageStats = { ...this.usageStats, ...JSON.parse(stats) };
      }
    } catch (error) {
      console.warn('Failed to load persistent cache:', error);
    }
  }

  // Save cache to sessionStorage
  savePersistentCache() {
    try {
      const cacheData = {};
      for (const [key, entry] of this.mapCache.entries()) {
        cacheData[key] = entry;
      }
      sessionStorage.setItem('mapboxCache', JSON.stringify(cacheData));
      sessionStorage.setItem('mapboxStats', JSON.stringify(this.usageStats));
    } catch (error) {
      console.warn('Failed to save persistent cache:', error);
    }
  }

  // Helper method to create a map with caching support
  createMapWithCache(container, mapOptions, id, newestJobUpdatedAt = null) {
    const mapboxgl = window.mapboxgl;
    
    // Try to get cached map first
    const cached = this.getCachedMap(mapOptions, newestJobUpdatedAt);
    if (cached) {
      // Reuse cached map configuration
      const map = new mapboxgl.Map({
        container: container,
        ...cached.mapData
      });
      this.registerMap(id, { map, markers: [], popups: [] });
      return map;
    }

    // Create new map and cache it
    const map = new mapboxgl.Map({
      container: container,
      ...mapOptions
    });
    this.cacheMap(id, mapOptions);
    this.registerMap(id, { map, markers: [], popups: [] });
    return map;
  }

  // Register a map for lazy loading
  registerLazyMap(container, mapOptions, id, callback, accessToken = null) {
    if (!this.intersectionObserver) {
      // Fallback: load immediately if IntersectionObserver not supported
      this.loadLazyMap(container, mapOptions, id, callback, accessToken);
      return;
    }

    // Store lazy map configuration
    this.lazyMaps.set(container, { mapOptions, id, callback, accessToken });

    // Start observing the container
    this.intersectionObserver.observe(container);
  }

  // Load a lazy map when it becomes visible
  async loadLazyMap(container, mapOptions = null, id = null, callback = null, accessToken = null) {
    // Get stored configuration if not provided
    const stored = this.lazyMaps.get(container);
    if (stored) {
      mapOptions = stored.mapOptions;
      id = stored.id;
      callback = stored.callback;
      accessToken = stored.accessToken ?? accessToken;
      this.lazyMaps.delete(container);
    }

    if (!mapOptions || !id) {
      return;
    }

    try {
      // Prefer the token passed at registration (Stimulus values); fall back to data-access-token
      await this.loadAPI(accessToken || container.dataset.accessToken);

      // Create map with caching
      const map = this.createMapWithCache(container, mapOptions, id);

      // Track lazy load
      this.trackLazyLoad();

      // Call callback if provided
      if (callback && typeof callback === 'function') {
        callback(map);
      }

      // Stop observing this container
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(container);
      }
    } catch (error) {
      console.error('Failed to load lazy map:', error);
      this.usageStats.errors++;
    }
  }

  // Create a marker
  createMarker(options = {}) {
    return new window.mapboxgl.Marker(options);
  }

  // Create a popup
  createPopup(options = {}) {
    return new window.mapboxgl.Popup(options);
  }

  // Load mapbox-gl-geocoder from CDN (unpkg) to ensure version consistency with mapbox-gl
  async loadGeocoder() {
    if (this.geocoderLoaded) {
      return Promise.resolve();
    }

    if (this.geocoderLoadingPromise) {
      return this.geocoderLoadingPromise;
    }

    this.geocoderLoadingPromise = new Promise((resolve, reject) => {
      // Check if geocoder is already loaded
      if (typeof window.MapboxGeocoder !== 'undefined') {
        this.geocoderLoaded = true;
        resolve();
        return;
      }

      // Skip loading geocoder CSS - using custom styles instead
      const geocoderVersion = '2.0.1';

      // Load geocoder JavaScript from unpkg CDN
      const jsUrl = `https://unpkg.com/mapbox-gl-geocoder@${geocoderVersion}/dist/mapbox-gl-geocoder.min.js`;
      const script = document.createElement('script');
      script.src = jsUrl;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        try {
          // MapboxGeocoder should be available on window after loading
          // UMD build from unpkg exposes it as window.MapboxGeocoder
          if (typeof window.MapboxGeocoder === 'undefined') {
            throw new Error('MapboxGeocoder not found on window after loading');
          }
          this.geocoderLoaded = true;
          resolve();
        } catch (error) {
          this.geocoderLoadingPromise = null;
          this.usageStats.errors++;
          reject(new Error(`Failed to initialize Mapbox Geocoder: ${error.message}`));
        }
      };

      script.onerror = () => {
        this.geocoderLoadingPromise = null;
        this.usageStats.errors++;
        reject(new Error('Failed to load Mapbox Geocoder from CDN'));
      };

      document.head.appendChild(script);
    });

    return this.geocoderLoadingPromise;
  }

  // Create a geocoder control
  async createGeocoder(options = {}) {
    // Ensure geocoder is loaded before creating instance
    await this.loadGeocoder();

    return new window.MapboxGeocoder({
      accessToken: window.mapboxgl.accessToken,
      mapboxgl: window.mapboxgl,
      ...options
    });
  }
}

const mapboxManager = new MapboxManager();

export default mapboxManager;
export { MapboxManager };

